import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import {
  collection,
  doc,
  Firestore,
  getDocs,
  limit,
  orderBy,
  query,
  QueryConstraint,
  startAfter,
  where,
  writeBatch
} from '@angular/fire/firestore';
import { Notification } from "../models/notification.model";
import { TenantService } from "./tenant.service";
import { MessagesService } from "../shared/services/messages.service";
import { Timestamp } from "@firebase/firestore";

@Injectable({
  providedIn: 'root'
})
export class UserNotificationsService {
  private tenant = inject(TenantService);
  private messages = inject(MessagesService);
  private firestore = inject(Firestore);
  private injector = inject(Injector);

  notifications: Notification[] = [];

  invalidateCache() {
    this.notifications = [];
  }

  async loadNotificationsPage(pageNumber: number, userId: string, lastPostDoc?: Timestamp): Promise<Notification[]> {
    try {
      if (!lastPostDoc && this.notifications.length > 0) {
        return this.notifications;
      }

      const constraints: QueryConstraint[] = [
        where("sentToUserId", "==", userId),
        orderBy("createdAt", "desc"),
        limit(pageNumber)
      ];

      if (lastPostDoc) {
        constraints.push(startAfter(lastPostDoc));
      }

      const userNotificationsQuery = query(
        collection(this.firestore, `schools/${this.tenant.id}/userNotifications`),
        ...constraints
      );

      const snapshot = await runInInjectionContext(this.injector, () => getDocs(userNotificationsQuery));
      const userNotifications: Notification[] = snapshot.docs.map(d => d.data() as Notification);

      this.notifications = [...this.notifications, ...userNotifications];

      return userNotifications;
    } catch (error) {
      const message = `Error loading all user notifications for userId=${userId}`;
      console.error(message, error);
      this.messages.error(message);
      return [];
    }
  }

  async markAsRead(notification: Notification) {
    const { batch, ref } = runInInjectionContext(this.injector, () => ({
      batch: writeBatch(this.firestore),
      ref: doc(this.firestore, `schools/${this.tenant.id}/userNotifications/${notification.id}`),
    }));

    batch.update(ref, notification);
    await batch.commit();
    this.invalidateCache();
  }

  async markAllAsRead(notifications: Notification[]) {
    const batch = runInInjectionContext(this.injector, () => writeBatch(this.firestore));

    notifications.forEach(notification => {
      const ref = runInInjectionContext(this.injector, () =>
        doc(this.firestore, `schools/${this.tenant.id}/userNotifications/${notification.id}`)
      );
      batch.update(ref, notification);
    });

    await batch.commit();
    this.invalidateCache();
  }

  async zeroOutNotificationsCounter(userId: string) {
    const { batch, ref } = runInInjectionContext(this.injector, () => ({
      batch: writeBatch(this.firestore),
      ref: doc(this.firestore, `schools/${this.tenant.id}/users/${userId}`),
    }));

    batch.update(ref, { unreadNotificationsCounter: 0 });
    await batch.commit();
  }
}
